import React from 'react';
import { HeadingElement } from './HeadingElement';
import { TextElement } from './TextElement';
import { MarkdownTextElement } from './MarkdownTextElement';
import { TableElement } from './TableElement';
import { ProductCard } from './ProductCard';
import { ProductList } from './ProductList';
import { ProductSearchResults } from './ProductSearchResults';
import { ProductSearchResultSet } from './ProductSearchResultSet';
import { ShowMoreProductResults } from './ShowMoreProductResults';
import { VendorCard } from './VendorCard';
import { VendorList } from './VendorList';
import { SmartBuyAnalysis } from './SmartBuyAnalysis';
import { CategorizedProducts } from './CategorizedProducts';
import { ClarifyingQuestions } from './ClarifyingQuestions';
import { SupportForm } from './SupportForm';

interface FormattedElementsProps {
  elements: any[];
  onProductClick: (productId: number) => void;
  onSendMessage?: (message: string) => void;
  isLastAgentMessage?: boolean;
  agent_chat_id?: number;
}

export const FormattedElements: React.FC<FormattedElementsProps> = ({ elements, onProductClick, onSendMessage, isLastAgentMessage = false, agent_chat_id }) => {
  return (
    <div className="space-y-3">
      {elements.map((element, index) => {
        switch (element.type) {
          case 'heading':
            return <HeadingElement key={index} text={element.text} />;

          case 'text':
            return <TextElement key={index} text={element.text} />;

          case 'markdown_text':
            return <MarkdownTextElement key={index} markdown={element.markdown} />;

          case 'table':
            return <TableElement key={index} table={element.table} />;

          case 'product_card':
            return (
              <ProductCard
                key={index}
                product_id={element.product_id}
                photo={element.photo}
                title={element.title}
                vendor={element.vendor}
                city={element.city}
                price={element.price}
                rating={element.rating}
                reviews={element.reviews}
                explanation={element.explanation}
                salampay_installments={element.salampay_installments}
                agent_chat_id={agent_chat_id}
              />
            );

          case 'product_list':
          case 'product_grid':
            return (
              <ProductList
                key={index}
                products={element.products}
                explanation={element.explanation}
                type={element.type}
                onProductClick={onProductClick}
                agent_chat_id={agent_chat_id}
              />
            );

          case 'product_search_result':
            return (
              <ProductSearchResults
                key={index}
                products={element.products}
                onProductClick={onProductClick}
                agent_chat_id={agent_chat_id}
              />
            );

          case 'product_search_result_set':
            return (
              <ProductSearchResultSet
                key={index}
                products={element.products}
                onProductClick={onProductClick}
                agent_chat_id={agent_chat_id}
              />
            );

          case 'show_more_product_results':
            return (
              <ShowMoreProductResults
                key={index}
                products={element.products}
                onProductClick={onProductClick}
                agent_chat_id={agent_chat_id}
              />
            );

          case 'vendor_card':
            return (
              <div className="w-full app-sm:max-w-md">
                <VendorCard
                  key={index}
                  vendor_id={element.vendor_id}
                  link={element.link}
                  photo={element.photo}
                  name={element.name}
                  city={element.city}
                  province={element.province}
                  rating={element.rating}
                  reviews={element.reviews}
                  description={element.description}
                />
              </div>
            );

          case 'vendor_list':
            return (
              <VendorList
                key={index}
                vendors={element.vendors}
              />
            );

          case 'smartbuy_analysis':
            return (
              <SmartBuyAnalysis
                key={index}
                data={element}
                onProductClick={onProductClick}
              />
            );

          case 'categorized_products':
            return (
              <CategorizedProducts
                key={index}
                categories={element.categories}
                onSendMessage={onSendMessage || (() => {})}
                agent_chat_id={agent_chat_id}
              />
            );

          case 'clarifying_questions':
            return (
              <ClarifyingQuestions
                key={index}
                questions={element.questions}
                onSendMessage={onSendMessage || (() => {})}
                isLastAgentMessage={isLastAgentMessage}
              />
            );

          case 'support_form':
            return (
              <SupportForm
                key={index}
                topics={element.topics}
                actor_type={element.actor_type}
                onSendMessage={onSendMessage || (() => {})}
                isLastAgentMessage={isLastAgentMessage}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
};
